import type { TimestampInput } from './timestamp';

export const TEMPORARY_CLOSURE_REASON_CODES = [
  'holiday',
  'vacation',
  'weather',
  'maintenance',
  'emergency',
  'private_event',
  'staffing',
  'other',
] as const;

export const SCHEDULE_EXCEPTION_TYPES = [
  'holiday',
  'planned_closure',
  'special_hours',
  'temporary_closure',
] as const;

export const SCHEDULE_TYPES = [
  'closed',
  'normal_hours',
  'custom_hours',
  'modified_hours',
  'extended_hours',
] as const;

export type TemporaryClosureReasonCode = (typeof TEMPORARY_CLOSURE_REASON_CODES)[number];
export type ScheduleExceptionType = (typeof SCHEDULE_EXCEPTION_TYPES)[number];
export type ScheduleType = (typeof SCHEDULE_TYPES)[number];
export type ExistingAppointmentsPolicy = 'review_required' | 'keep_existing' | 'cancel_and_notify';
export type ScheduleExceptionStatus = 'scheduled' | 'active' | 'completed' | 'cancelled';

export interface ScheduleExceptionInterval {
  start: string;
  end: string;
}

export interface ScheduleExceptionRecurrence {
  type: 'none' | 'yearly';
  month?: number;
  day?: number;
  endMonth?: number;
  endDay?: number;
}

export interface ScheduleExceptionVisibility {
  showPublicNotice: boolean;
  publicLabel?: string;
  publicMessage?: string;
}

export interface ScheduleException {
  id: string;
  shopId: string;
  type: ScheduleExceptionType;
  source?: 'shop' | 'member';
  name?: string;
  date?: string;
  endDate?: string;
  timezone: string;
  scheduleType: ScheduleType;
  intervals: ScheduleExceptionInterval[];
  recurrence: ScheduleExceptionRecurrence;
  visibility: ScheduleExceptionVisibility;
  status: ScheduleExceptionStatus;
  version: number;
  createdBy: string;
  createdAt: TimestampInput;
  updatedAt: TimestampInput;
  operationId: string;

  // Legacy temporary-closure fields kept for compatibility while the unified
  // schedule-exception engine is rolled out.
  closureType?: 'FULL_DAY' | 'DATE_RANGE' | 'PARTIAL_DAY';
  title?: string;
  reasonCode?: TemporaryClosureReasonCode;
  startAt?: TimestampInput;
  endAt?: TimestampInput;
  allDay?: boolean;
  bookingPolicy?: { blockNewBookings: boolean; blockWalkIns: boolean };
  appointmentHandling: { existingAppointmentsPolicy: ExistingAppointmentsPolicy };
  affectedAppointmentCount: number;
  cancelledAt?: TimestampInput;
}
